import { NavLink, useNavigate, Outlet, Navigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Loader2 } from "lucide-react";
import { Search, MessagesSquare, LogOut, Menu, Building2, Star, Home, HelpCircle } from "lucide-react";

const NAV = [
  { to: "/foretag", label: "Sök kandidater", icon: Search, end: true },
  { to: "/foretag/sparade", label: "Sparade kandidater", icon: Star },
  { to: "/sa-funkar-det?for=arbetsgivare", label: "Så funkar det", icon: HelpCircle },
  { to: "/foretag/meddelanden", label: "Meddelanden", icon: MessagesSquare },
];

function NavItems({ onNavigate, msgBadge = 0 }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map((item) => {
        const Icon = item.icon;
        const badge = item.to === "/foretag/meddelanden" ? msgBadge : 0;
        return (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
            data-testid={`emp-nav-${item.to.replace(/\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isActive ? "bg-primary/10 text-primary font-semibold" : "text-foreground/70 hover:bg-muted hover:text-foreground"
              }`}>
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{item.label}</span>
            {badge > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold text-white" data-testid="emp-msg-badge">{badge}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3 px-5 py-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Building2 className="h-5 w-5" /></div>
      <div className="leading-tight">
        <div className="font-head font-bold text-lg text-primary">HAMN Match</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">För arbetsgivare</div>
      </div>
    </div>
  );
}

export default function EmployerLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (!user || (user.role !== "employer" && user.role !== "admin")) return;
    const load = () => api.get("/notifications").then((r) => setMsgUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center hamn-bg"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/foretag/logga-in" replace />;
  if (user.role !== "employer" && user.role !== "admin") return <Navigate to="/app" replace />;

  const onLogout = () => { logout(); navigate("/foretag/logga-in"); };

  return (
    <div className="hamn-bg min-h-screen">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col border-r border-border bg-white/80 backdrop-blur-xl">
        <Brand />
        <div className="flex-1 overflow-y-auto pb-4"><NavItems msgBadge={msgUnread} /></div>
        <div className="border-t border-border p-4">
          <div className="mb-3 rounded-xl bg-muted px-3 py-2">
            <div className="text-xs text-muted-foreground">Inloggad som</div>
            <div className="text-sm font-medium truncate">{user?.company_name || user?.name}</div>
            <div className="text-[11px] font-mono text-primary">{user?.candidate_id}</div>
          </div>
          <Link to="/" data-testid="emp-home-btn">
            <Button variant="ghost" className="w-full rounded-full mb-2 text-muted-foreground"><Home className="h-4 w-4 mr-2" /> Till startsidan</Button>
          </Link>
          <Button data-testid="emp-logout-btn" variant="outline" className="w-full rounded-full" onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Logga ut
          </Button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-white/90 backdrop-blur-xl px-4 py-3">
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="emp-mobile-menu-btn" variant="ghost" size="icon"><Menu /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <Brand />
            <div className="overflow-y-auto h-[calc(100vh-160px)]"><NavItems msgBadge={msgUnread} /></div>
            <div className="p-4 border-t border-border space-y-2">
              <Link to="/"><Button variant="ghost" className="w-full rounded-full text-muted-foreground"><Home className="h-4 w-4 mr-2" /> Till startsidan</Button></Link>
              <Button variant="outline" className="w-full rounded-full" onClick={onLogout}><LogOut className="h-4 w-4 mr-2" /> Logga ut</Button>
            </div>
          </SheetContent>
        </Sheet>
        <div className="font-head font-bold text-primary">HAMN Match</div>
        <div className="text-[11px] font-mono text-primary">{user?.candidate_id}</div>
      </header>

      <main className="lg:pl-72">
        <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10"><Outlet /></div>
      </main>
    </div>
  );
}
